import { CenterSpin } from '@/components/CenterSpin';
import { ErrorPage } from '@/components/ErrorPage';
import { type ActionConfig, PageHeader } from '@/components/PageHeader';
import {
  type ValidationResult,
  useCreatePipelineConfigurationVersion,
  usePipelineById,
  usePipelineConfigurationVersionById,
  usePipelineConfigurationVersionsByPipelineId,
  useUpdatePipelineConfigurationVersion,
} from '@/queries/pipelines';
import { getErrorMessage, logger } from '@/utils';
import { usePersistentState } from '@/utils/hooks';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import App from 'antd/es/app';
import Segmented from 'antd/es/segmented';
import Select from 'antd/es/select';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { css, styled } from 'styled-components';
import { FullHeightContent, InteractivePageLayout } from 'ui-components';

import type { PipelineBlock, PipelineConfiguration } from '@mai/types';

import { ContextConfigurationPanel } from './components/ContextConfigurationPanel';
import { RunModal } from './components/RunModal';
import { ValidationStatus } from './components/ValidationStatus';
import { WorkflowCanvas } from './components/WorkflowCanvas';
import { YamlEditor } from './components/YamlEditor';
import { pipelineConfigToYaml, yamlToPipelineConfigBestEffort } from './utils';
import { convertValidationErrorsToDiagnostics } from './utils/yamlDiagnostics';

const Container = styled.div<{ $fullScreen: boolean }>`
  ${({ theme, $fullScreen }) => css`
    height: calc(100vh - 70px);
    display: flex;
    flex-direction: column;
    max-width: ${$fullScreen ? '100%' : `${theme.screenXL}px`};
    margin: 0 auto;
    transition: all ${theme.motionDurationMid} ease;
    width: ${$fullScreen ? '100%' : 'auto'};
    box-sizing: border-box;
  `}
`;

const WorkbenchLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginSM}px;
  width: 100%;
  height: 100%;
  min-width: 200px;
  container-type: inline-size;
  container-name: workbench;

  @container workbench (max-width: ${({ theme }) => theme.screenMD}px) {
    height: 100%;
    min-height: 0;
  }

  /* Very small screens - reduce min-width for better mobile experience */
  @container workbench (max-width: 400px) {
    min-width: 320px;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  min-width: 200px;

  @container workbench (max-width: ${({ theme }) => theme.screenMD}px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.marginSM}px;
    height: auto;
    min-height: 0;
    max-height: none;
  }

  /* Very small screens - reduce min-width for better mobile experience */
  @container workbench (max-width: 400px) {
    min-width: 320px;
  }
`;

const EditorSection = styled.div<{ mode: 'yaml' | 'gui' }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: ${({ mode }) => (mode === 'yaml' ? '3' : '0')};
  min-width: 0;
  opacity: ${({ mode }) => (mode === 'yaml' ? 1 : 0)};
  margin-right: ${({ mode, theme }) =>
    mode === 'yaml' ? theme.marginLG + 'px' : '0'};
  transition:
    flex ${({ theme }) => theme.motionDurationMid}
      ${({ theme }) => theme.motionEaseInOut},
    opacity ${({ theme }) => theme.motionDurationFast}
      ${({ theme }) => theme.motionEaseInOut},
    margin ${({ theme }) => theme.motionDurationMid}
      ${({ theme }) => theme.motionEaseInOut};

  @container workbench (max-width: ${({ theme }) => theme.screenMD}px) {
    order: ${({ mode }) => (mode === 'yaml' ? '2' : '4')};
    flex: ${({ mode }) => (mode === 'yaml' ? '0 0 350px' : '0')};
    margin-right: 0;
    min-height: ${({ mode }) => (mode === 'yaml' ? '350px' : '0')};
    max-height: ${({ mode }) => (mode === 'yaml' ? '350px' : '0')};
    overflow: ${({ mode }) => (mode === 'yaml' ? 'hidden' : 'visible')};
  }
`;

const VisualizationSection = styled.div<{ mode: 'yaml' | 'gui' }>`
  display: flex;
  flex-direction: column;
  min-height: 300px;
  overflow: visible;
  flex: ${({ mode }) => (mode === 'yaml' ? '2' : '1')};
  flex-basis: 0;
  min-width: 200px;
  contain: layout;
  transition: flex ${({ theme }) => theme.motionDurationMid}
    ${({ theme }) => theme.motionEaseInOut};

  @container workbench (max-width: ${({ theme }) => theme.screenMD}px) {
    order: 1;
    flex: 0 0 350px;
    min-height: 350px;
    max-height: 350px;
    min-width: 200px;
    opacity: 1;
    overflow: hidden;
  }

  /* Very small screens - reduce min-width for better mobile experience */
  @container workbench (max-width: 400px) {
    min-width: 280px;
  }
`;

const ContextSection = styled.div<{ mode: 'yaml' | 'gui' }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: ${({ mode }) => (mode === 'gui' ? '0 0 auto' : '0')};
  width: ${({ mode }) => (mode === 'gui' ? 'clamp(300px, 25vw, 400px)' : '0')};
  min-width: 0;
  opacity: ${({ mode }) => (mode === 'gui' ? 1 : 0)};
  margin-left: ${({ mode, theme }) =>
    mode === 'gui' ? theme.marginLG + 'px' : '0'};
  transition:
    flex ${({ theme }) => theme.motionDurationMid}
      ${({ theme }) => theme.motionEaseInOut},
    width ${({ theme }) => theme.motionDurationMid}
      ${({ theme }) => theme.motionEaseInOut},
    opacity ${({ theme }) => theme.motionDurationFast}
      ${({ theme }) => theme.motionEaseInOut},
    margin ${({ theme }) => theme.motionDurationMid}
      ${({ theme }) => theme.motionEaseInOut};

  @container workbench (max-width: ${({ theme }) => theme.screenMD}px) {
    order: ${({ mode }) => (mode === 'gui' ? '3' : '5')};
    flex: ${({ mode }) => (mode === 'gui' ? '0 0 350px' : '0')};
    width: auto;
    margin-left: 0;
    min-height: ${({ mode }) => (mode === 'gui' ? '350px' : '0')};
    max-height: ${({ mode }) => (mode === 'gui' ? '350px' : '0')};
    overflow: ${({ mode }) => (mode === 'gui' ? 'hidden' : 'visible')};
  }
`;

const HeaderWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.marginSM}px;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.marginSM}px;
`;

const VersionSelect = styled(Select<string>)`
  width: 140px;
`;

export const PipelineWorkbenchV2Page: React.FC = () => {
  const { teamId, pipelineId } = useParams<{
    teamId: string;
    pipelineId: string;
  }>();
  const { message } = App.useApp();

  const [searchParams, setSearchParams] = useSearchParams();
  const [pipelineConfig, setPipelineConfig] =
    useState<PipelineConfiguration | null>(null);
  const [userYamlContent, setUserYamlContent] = useState<string>('');
  const [userModified, setUserModified] = useState(false);
  const [isInitiatingExecution, setIsInitiatingExecution] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [bestEffortWarnings, setBestEffortWarnings] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  // Run modal state from search params
  const runModalOpen = searchParams.get('runModal') === 'true';
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [fullScreen, setFullScreen] = usePersistentState('fullScreen', false);
  const [nodePositions, setNodePositions] = useState<
    Map<string, { x: number; y: number }>
  >(new Map());

  // Editor mode via search params
  const editorMode =
    (searchParams.get('editorMode') as 'yaml' | 'gui') || 'gui';
  const setEditorMode = (mode: 'yaml' | 'gui') => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('editorMode', mode);
        return newParams;
      },
      { replace: true },
    );
  };

  // Derive canonical YAML from pipeline configuration (for initial sync)
  const canonicalYamlContent = useMemo(() => {
    if (!pipelineConfig) return '';
    try {
      return pipelineConfigToYaml(pipelineConfig);
    } catch (error) {
      logger.error('Error converting pipeline config to YAML:', error);
      return '# Error: Unable to convert pipeline configuration to YAML';
    }
  }, [pipelineConfig]);

  // Extract input schema from validation result
  const inputSchema = useMemo(() => {
    return validationResult?.inputSchema || null;
  }, [validationResult]);

  // Convert validation errors to YAML editor diagnostics
  const yamlDiagnostics = useMemo(() => {
    if (!validationResult || !userYamlContent) {
      return [];
    }

    return convertValidationErrorsToDiagnostics(
      userYamlContent,
      validationResult.errors,
      validationResult.warnings,
    );
  }, [validationResult, userYamlContent]);

  // Sync user YAML with canonical YAML when config loads/changes and user hasn't modified
  useEffect(() => {
    if (
      canonicalYamlContent &&
      !userModified &&
      canonicalYamlContent !== userYamlContent
    ) {
      setUserYamlContent(canonicalYamlContent);
    }
  }, [canonicalYamlContent, userModified, userYamlContent]);

  const createPipelineConfigurationMutation =
    useCreatePipelineConfigurationVersion();
  const updatePipelineConfigurationMutation =
    useUpdatePipelineConfigurationVersion();
  const setActiveMutation = useUpdatePipelineConfigurationVersion();

  // Get current version from URL search params
  const currentVersionId = searchParams.get('configVersionId');

  // Track execution status via callback from RunPipeline component
  const [isExecutionRunning, setIsExecutionRunning] = useState(false);

  const {
    data: pipeline,
    isLoading: isPipelineLoading,
    error: pipelineError,
  } = usePipelineById(pipelineId || null, {
    enabled: !!pipelineId,
  });

  const { data: configVersions, isLoading: isConfigVersionsLoading } =
    usePipelineConfigurationVersionsByPipelineId(pipelineId || null, {
      enabled: !!pipelineId,
    });

  // Auto-select current version if none is selected in URL
  const effectiveVersionId = useMemo(() => {
    // If there's already a version in the URL, use it
    if (currentVersionId) {
      return currentVersionId;
    }

    // Otherwise, find the current version but don't update URL automatically
    if (configVersions?.items.length) {
      const currentVersion = configVersions.items.find(
        (v) => v.status === 'current',
      );
      return currentVersion?.pipelineConfigurationVersionId || null;
    }

    return null;
  }, [currentVersionId, configVersions?.items]);

  const { data: configVersion, isLoading: isConfigVersionLoading } =
    usePipelineConfigurationVersionById(effectiveVersionId, {
      enabled: !!effectiveVersionId,
    });

  // Determine if the user has made changes (either to YAML or config)
  const hasPendingChanges = useMemo(() => {
    // If user has modified anything, we have pending changes
    if (userModified) {
      return true;
    }

    // Fallback: check if parsed config differs from saved version
    if (!pipelineConfig || !configVersion?.configuration) {
      return false;
    }

    return (
      JSON.stringify(pipelineConfig) !==
      JSON.stringify(configVersion.configuration)
    );
  }, [userModified, pipelineConfig, configVersion?.configuration]);

  // Check if current version is already active
  const isCurrentVersionActive = useMemo(() => {
    return configVersion?.status === 'current';
  }, [configVersion?.status]);

  // Load pipeline configuration when config version changes
  useEffect(() => {
    if (configVersion?.configuration) {
      setPipelineConfig(configVersion.configuration);
      setUserModified(false);
    } else if (!configVersion && !effectiveVersionId && pipeline) {
      // Initialize empty pipeline config for new pipelines without versions
      const emptyConfig: PipelineConfiguration = {
        id: `pipeline_${pipelineId}`,
        name: pipeline.name || 'New Pipeline',
        version: '1.0.0',
        description: pipeline.description || '',
        blocks: {},
        connections: [],
      };
      setPipelineConfig(emptyConfig);
      setUserModified(false);
      logger.info('Initialized empty pipeline configuration');
    }
  }, [configVersion, effectiveVersionId, pipeline, pipelineId]);

  // Clear initiation state when execution starts running
  useEffect(() => {
    if (isInitiatingExecution && isExecutionRunning) {
      setIsInitiatingExecution(false);
    }
  }, [isInitiatingExecution, isExecutionRunning]);

  const handleVersionChange = (versionId: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('configVersionId', versionId);
        return newParams;
      },
      { replace: true },
    );
    // Reset user modification state when switching versions
    // The useEffect for configVersion will handle setting the new YAML content
    setUserModified(false);
  };

  const handlePipelineConfigChange = useCallback(
    (config: PipelineConfiguration) => {
      setPipelineConfig(config);
      setUserModified(true);
    },
    [setPipelineConfig, setUserModified],
  );

  // Create debounce utility
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Optimized config update that only recreates the changed block
  const updateBlockConfigOptimized = useCallback(
    (blockId: string, newConfig: PipelineBlock['config']) => {
      if (!pipelineConfig) return;

      // Check if block exists
      if (!pipelineConfig.blocks[blockId]) return;

      // Update the specific block's config
      const updatedConfig = {
        ...pipelineConfig,
        blocks: {
          ...pipelineConfig.blocks,
          [blockId]: {
            ...pipelineConfig.blocks[blockId],
            config: newConfig,
          },
        },
      };

      handlePipelineConfigChange(updatedConfig as PipelineConfiguration);
    },
    [pipelineConfig, handlePipelineConfigChange],
  );

  // Debounced version to reduce pipeline rebuilds during active editing
  const handleBlockConfigChange = useCallback(
    (blockId: string, newConfig: PipelineBlock['config']) => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout - config changes will only apply after 300ms of inactivity
      debounceTimeoutRef.current = setTimeout(() => {
        updateBlockConfigOptimized(blockId, newConfig);
      }, 300);
    },
    [updateBlockConfigOptimized],
  );

  // Handler for block metadata changes (name and description)
  const handleBlockMetadataChange = useCallback(
    (blockId: string, metadata: { name?: string; description?: string }) => {
      if (!pipelineConfig) return;
      // Check if block exists
      if (!pipelineConfig.blocks[blockId]) return;
      // Update the specific block's metadata
      const updatedConfig = {
        ...pipelineConfig,
        blocks: {
          ...pipelineConfig.blocks,
          [blockId]: {
            ...pipelineConfig.blocks[blockId],
            name: metadata.name,
            description: metadata.description,
          },
        },
      };
      setPipelineConfig(updatedConfig);
      setUserModified(true);
    },
    [pipelineConfig],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleYamlContentChange = (yamlContent: string) => {
    setUserYamlContent(yamlContent);
    setUserModified(true);

    // Only try to parse non-empty YAML content
    if (!yamlContent.trim()) {
      setBestEffortWarnings([]);
      return;
    }

    // Use best-effort parsing to extract as much valid configuration as possible
    // This enables real-time React Flow updates even with validation errors
    try {
      const parseResult = yamlToPipelineConfigBestEffort(yamlContent);
      if (parseResult.success && parseResult.data) {
        setPipelineConfig(parseResult.data);
        setBestEffortWarnings(parseResult.warnings || []);
      } else {
        setBestEffortWarnings([]);
      }
      // Note: We don't clear userModified on successful parse
      // The user is still in "editing mode" until they save or reset
    } catch (error) {
      // On parse errors, keep the user YAML but don't update config
      // This allows ValidationStatus to show the error
      setBestEffortWarnings([]);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!pipelineId) {
      void message.error('No pipeline ID available');
      return;
    }

    // Try to get a best-effort configuration from current YAML
    let configToSave = pipelineConfig;

    if (!configToSave && userYamlContent.trim()) {
      // If we don't have a parsed config but have YAML content, try best-effort parsing
      try {
        const parseResult = yamlToPipelineConfigBestEffort(userYamlContent);
        if (parseResult.success && parseResult.data) {
          configToSave = parseResult.data;
          // Show warning about validation issues
          if (parseResult.warnings && parseResult.warnings.length > 0) {
            void message.warning(
              'Configuration has validation issues but will be saved',
            );
          }
        }
      } catch (error) {
        void message.error('Unable to parse YAML configuration');
        return;
      }
    }

    if (!configToSave) {
      void message.error('No valid configuration to save');
      return;
    }

    try {
      // If no version is selected (no versions exist), create a new one
      if (!effectiveVersionId) {
        const newVersion =
          await createPipelineConfigurationMutation.mutateAsync({
            pipelineId,
            configuration: configToSave,
            status: 'current', // Set as current since it's the first version
          });

        // Update URL to include the new version ID
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set(
              'configVersionId',
              newVersion.pipelineConfigurationVersionId,
            );
            return newParams;
          },
          { replace: true },
        );

        void message.success('Configuration created successfully');
      } else {
        // Update existing version
        await updatePipelineConfigurationMutation.mutateAsync({
          pipelineConfigurationVersionId: effectiveVersionId,
          data: {
            configuration: configToSave,
          },
        });

        void message.success('Configuration saved successfully');
      }

      setUserModified(false); // Reset modification state after successful save
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to save configuration',
      );
      void message.error(errorMessage);
    }
  };

  const handleSetActive = async () => {
    if (!effectiveVersionId || !configVersions) {
      void message.error('No configuration version selected');
      return;
    }

    try {
      // First, find any existing 'current' version and set it to 'archived'
      const existingCurrentVersion = configVersions.items.find(
        (v) => v.status === 'current',
      );

      if (existingCurrentVersion) {
        await setActiveMutation.mutateAsync({
          pipelineConfigurationVersionId:
            existingCurrentVersion.pipelineConfigurationVersionId,
          data: {
            status: 'archived',
          },
        });
      }

      // Then set the selected version as 'current'
      await setActiveMutation.mutateAsync({
        pipelineConfigurationVersionId: effectiveVersionId,
        data: {
          status: 'current',
        },
      });

      void message.success('Version set as active successfully');
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to set version as active',
      );
      void message.error(errorMessage);
    }
  };

  const handleRunPipeline = async () => {
    if (!effectiveVersionId) {
      void message.error('No configuration version selected');
      return;
    }

    // Always open the run modal for consistent UX
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('runModal', 'true');
      return newParams;
    });
  };

  const handleExecutionStatusChange = (isRunning: boolean) => {
    setIsExecutionRunning(isRunning);
  };

  // Handle node selection/deselection
  const handleNodeSelection = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId || null);
    // Clear edge selection when selecting a node
    if (nodeId) {
      setSelectedEdgeId(null);
    }
  }, []);

  // Handle edge selection/deselection
  const handleEdgeSelection = useCallback((edgeId: string) => {
    setSelectedEdgeId(edgeId || null);
    // Clear node selection when selecting an edge
    if (edgeId) {
      setSelectedNodeId(null);
    }
  }, []);

  // Handle node deletion
  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      // Clear selection if the deleted node was selected
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }
    },
    [selectedNodeId],
  );

  // Handle escape key to clear selection and delete key to delete selected node
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.closest('.ant-input') !== null ||
        target.closest('.ant-input-wrapper') !== null ||
        target.closest('.monaco-editor') !== null ||
        target.closest('.json-editor') !== null;
      
      // Don't intercept keyboard events when user is typing
      if (isInputField) {
        return;
      }

      if (event.key === 'Escape' && selectedNodeId) {
        setSelectedNodeId(null);
      } else if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        selectedNodeId &&
        editorMode === 'gui'
      ) {
        // Only delete if we're in GUI mode and have a selected node
        handleNodeDelete(selectedNodeId);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNodeId, editorMode, handleNodeDelete]);

  if (!teamId || !pipelineId) {
    return (
      <ErrorPage
        title="Missing Information"
        message="Unable to load workbench. Required information is missing."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  if (isPipelineLoading) {
    return <CenterSpin />;
  }

  if (pipelineError) {
    return (
      <ErrorPage
        title="Error Loading Pipeline"
        message="There was an error loading the pipeline details. Please try again."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  if (!pipeline) {
    return (
      <ErrorPage
        title="Pipeline Not Found"
        message="The requested pipeline could not be found."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  // Create actions array for PageHeader - Run first so it's always visible
  const headerActions: ActionConfig[] = [
    {
      label: 'Run',
      icon:
        isInitiatingExecution || isExecutionRunning ? (
          <LoadingOutlined />
        ) : (
          <PlayCircleOutlined />
        ),
      type: 'primary',
      disabled:
        !effectiveVersionId || isInitiatingExecution || isExecutionRunning,
      onClick: handleRunPipeline,
    },
    {
      label: 'Save',
      icon: <SaveOutlined />,
      type: 'default',
      disabled: !hasPendingChanges && !!effectiveVersionId,
      onClick: handleSaveConfiguration,
    },
    {
      label: 'Set Active',
      icon: <CheckOutlined />,
      type: 'default',
      disabled: isCurrentVersionActive || !effectiveVersionId,
      onClick: handleSetActive,
    },
  ];

  return (
    <>
      <Container $fullScreen={fullScreen}>
        <HeaderWrapper>
          <PageHeader
            title={pipeline.name}
            breadcrumbs={[
              { label: 'Pipelines', path: `/team/${teamId}/pipelines` },
              {
                label: pipeline.name,
                path: `/team/${teamId}/pipelines/${pipelineId}`,
              },
              { label: 'Workbench' },
            ]}
            description={
              <DescriptionWrapper>
                <VersionSelect
                  value={effectiveVersionId}
                  onChange={handleVersionChange}
                  placeholder="Select version"
                  loading={isConfigVersionsLoading}
                  size="small"
                  options={
                    configVersions?.items.map((version) => ({
                      label: `${version.status === 'current' ? '✓ ' : ''}v${version.version || 'draft'}`,
                      value: version.pipelineConfigurationVersionId,
                    })) || []
                  }
                />
                <ValidationStatus
                  yamlContent={userYamlContent}
                  userModified={userModified}
                  onValidationResult={setValidationResult}
                  bestEffortWarnings={bestEffortWarnings}
                  onValidationStart={() => setIsValidating(true)}
                  onValidationComplete={() => setIsValidating(false)}
                />
                <Segmented
                  value={editorMode}
                  onChange={(value) => setEditorMode(value as 'yaml' | 'gui')}
                  options={[
                    { label: 'GUI', value: 'gui' },
                    { label: 'YAML', value: 'yaml' },
                  ]}
                  size="small"
                />
              </DescriptionWrapper>
            }
            actions={headerActions}
            showFullScreenToggle={true}
            fullScreen={fullScreen}
            onFullScreenToggle={setFullScreen}
          />
        </HeaderWrapper>
        <InteractivePageLayout>
          <FullHeightContent>
            <WorkbenchLayout>
              <TopSection>
                <EditorSection mode={editorMode}>
                  <YamlEditor
                    yamlContent={userYamlContent}
                    onYamlContentChange={handleYamlContentChange}
                    isLoading={isConfigVersionLoading}
                    diagnostics={yamlDiagnostics}
                  />
                </EditorSection>

                <VisualizationSection mode={editorMode}>
                  <WorkflowCanvas
                    mode={editorMode}
                    pipelineConfig={pipelineConfig}
                    onPipelineConfigChange={handlePipelineConfigChange}
                    onNodeClick={handleNodeSelection}
                    selectedNodeId={selectedNodeId}
                    onNodeDelete={handleNodeDelete}
                    onEdgeClick={handleEdgeSelection}
                    selectedEdgeId={selectedEdgeId}
                    nodePositions={nodePositions}
                    onNodePositionsChange={setNodePositions}
                  />
                </VisualizationSection>

                <ContextSection mode={editorMode}>
                  <ContextConfigurationPanel
                    selectedNodeId={selectedNodeId}
                    selectedEdgeId={selectedEdgeId}
                    pipelineConfig={pipelineConfig}
                    onBlockConfigChange={handleBlockConfigChange}
                    onBlockMetadataChange={handleBlockMetadataChange}
                    onPipelineConfigChange={handlePipelineConfigChange}
                    onNodeDeselect={() => setSelectedNodeId(null)}
                  />
                </ContextSection>
              </TopSection>
            </WorkbenchLayout>
          </FullHeightContent>
        </InteractivePageLayout>
      </Container>

      {/* Run Modal for pipeline execution */}
      <RunModal
        open={runModalOpen}
        onClose={() => {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('runModal');
            newParams.delete('runModalStep');
            return newParams;
          });
        }}
        pipelineId={pipelineId}
        configVersionId={effectiveVersionId || ''}
        inputSchema={inputSchema}
        pipelineConfig={pipelineConfig}
        isValidating={isValidating}
        teamId={teamId}
        onExecutionStatusChange={handleExecutionStatusChange}
      />
    </>
  );
};
